import { useState, useEffect, useRef, useCallback } from 'react';
import { X, UserPlus, ExternalLink } from 'lucide-react';
import type { UserNotification } from '../../contexts/NotificationContext';

interface LeadNotificationPopupProps {
  notification: UserNotification;
  onDismiss: (id: string) => void;
  onViewLead: (notification: UserNotification) => void;
  /** Queued leads beyond this one — rendered as a "+N more" footer. */
  moreCount?: number;
  onViewAll?: () => void;
}

const AUTO_DISMISS_MS = 12000;

/** Color-coded quality badge (also used by the consultant dashboard's New Leads panel) */
export function QualityBadge({ quality }: { quality?: string }) {
  if (!quality) return null;

  const config: Record<string, { bg: string; text: string; label: string }> = {
    hot: { bg: 'bg-red-100', text: 'text-red-700', label: 'Hot' },
    high: { bg: 'bg-orange-100', text: 'text-orange-700', label: 'High' },
    medium: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Medium' },
    low: { bg: 'bg-neutral-100', text: 'text-neutral-600', label: 'Low' },
  };

  const c = config[quality.toLowerCase()] || config.medium;

  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${c.bg} ${c.text}`}>
      {c.label}
    </span>
  );
}

export function LeadNotificationPopup({
  notification,
  onDismiss,
  onViewLead,
  moreCount = 0,
  onViewAll,
}: LeadNotificationPopupProps) {
  const [isExiting, setIsExiting] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const remainingRef = useRef(AUTO_DISMISS_MS);
  const startTimeRef = useRef(Date.now());

  const { data } = notification;
  const contactName = data?.contactName || 'Unknown';
  const contactPhone = data?.contactPhone || '';
  const contactEmail = data?.contactEmail || '';
  const source = data?.source || '';
  const developmentName = data?.developmentName || '';
  const leadScore = data?.leadScore;
  const leadQuality = data?.leadQuality;

  // No chime here — NotificationContext plays ONE chime per incoming batch
  // (three queued leads used to mean three overlapping chimes).

  const startDismissTimer = useCallback(() => {
    startTimeRef.current = Date.now();
    timerRef.current = setTimeout(() => {
      setIsExiting(true);
      setTimeout(() => onDismiss(notification.id), 300);
    }, remainingRef.current);
  }, [notification.id, onDismiss]);

  // Start auto-dismiss timer
  useEffect(() => {
    startDismissTimer();
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [startDismissTimer]);

  // Pause/resume timer on hover
  const handleMouseEnter = () => {
    setIsPaused(true);
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    const elapsed = Date.now() - startTimeRef.current;
    remainingRef.current = Math.max(remainingRef.current - elapsed, 1000);
  };

  const handleMouseLeave = () => {
    setIsPaused(false);
    startDismissTimer();
  };

  const handleClose = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setIsExiting(true);
    setTimeout(() => onDismiss(notification.id), 300);
  };

  const handleViewLead = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    onViewLead(notification);
  };


  return (
    <div
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={`
        fixed right-4 w-96 max-sm:left-4 max-sm:right-4 max-sm:w-auto
        bg-white/95 backdrop-blur-md
        border border-neutral-200 border-l-4 border-l-primary
        rounded-lg shadow-2xl overflow-hidden
        ${isExiting ? 'animate-slide-out-down' : 'animate-slide-in-up-bounce'}
      `}
      style={{
        bottom: '16px',
        // Below every modal (Modal 10001 / admin 10002 / toasts+bell 10003):
        // an informational toast must never cover interactive content.
        zIndex: 10000,
      }}
      role="alert"
      aria-live="assertive"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 pt-3 pb-1">
        <span className="text-xs font-bold uppercase tracking-wider text-primary">New Lead</span>
        <div className="flex items-center gap-2">
          <QualityBadge quality={leadQuality} />
          <button
            onClick={handleClose}
            className="p-0.5 rounded hover:bg-neutral-100 transition-colors text-neutral-400 hover:text-neutral-600"
            aria-label="Dismiss notification"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="px-4 pb-2">
        {/* Contact info */}
        <div className="flex items-center gap-2 mb-1.5">
          <UserPlus className="w-5 h-5 text-primary flex-shrink-0" />
          <span className="font-semibold text-neutral-900 text-sm truncate">{contactName}</span>
        </div>

        <div className="text-xs text-neutral-500 mb-2 truncate">
          {contactPhone && (
            <a href={`tel:${contactPhone}`} className="hover:text-primary transition-colors">
              {contactPhone}
            </a>
          )}
          {contactPhone && contactEmail && <span className="mx-1.5">&middot;</span>}
          {contactEmail && <span>{contactEmail}</span>}
        </div>

        {/* Metadata row */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-neutral-600 mb-3">
          {source && (
            <span>
              <span className="text-neutral-400">Source:</span> {source}
            </span>
          )}
          {developmentName && (
            <span>
              <span className="text-neutral-400">Dev:</span> {developmentName}
            </span>
          )}
          {leadScore != null && (
            <span>
              <span className="text-neutral-400">Score:</span> {leadScore}/100
            </span>
          )}
        </div>

        {/* View Lead button */}
        <button
          onClick={handleViewLead}
          className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 text-sm font-medium
            text-white bg-primary hover:bg-primary-dark rounded-md transition-colors"
        >
          View Lead
          <ExternalLink className="w-3.5 h-3.5" />
        </button>

        {/* Collapsed queue counter */}
        {moreCount > 0 && onViewAll && (
          <button
            onClick={onViewAll}
            className="w-full mt-1.5 text-center text-xs font-medium text-primary hover:text-primary-dark transition-colors"
          >
            +{moreCount} more new lead{moreCount === 1 ? '' : 's'} — View all
          </button>
        )}
      </div>

      {/* Progress bar */}
      <div className="h-1 bg-neutral-100">
        <div
          className="h-full bg-primary/40 rounded-r"
          style={{
            animation: isPaused ? 'none' : `shrink-width ${AUTO_DISMISS_MS / 1000}s linear forwards`,
            width: isPaused ? undefined : '100%',
          }}
        />
      </div>
    </div>
  );
}
